/**
 * Enum for different section types
 */
export enum SectionType {
  CODEBLOCK = 'codeblock',
  DEFAULT = 'default',
}

/**
 * Interface for section type detection result
 */
interface SectionTypeResult {
  type: SectionType;
  dataType?: string;
  element?: HTMLDivElement | null;
}

/**
 * Interface for parsed HTML element attributes
 */
interface ElementAttributes {
  id?: string;
  dataType?: string;
  dataBgColor?: string;
  dataPaddingTop?: string;
  dataPaddingRight?: string;
  dataPaddingBottom?: string;
  dataPaddingLeft?: string;
  dataMarginTop?: string;
  dataMarginRight?: string;
  dataMarginBottom?: string;
  dataMarginLeft?: string;
  [key: string]: string | undefined;
}

/**
 * Detect the type of section based on its content
 *
 * @param sectionContent - HTML content of the section
 * @returns SectionTypeResult - Result containing section type and metadata
 */
export const detectSectionType = (
  sectionContent: string,
): SectionTypeResult => {
  try {
    // Validate input
    if (!sectionContent || typeof sectionContent !== 'string') {
      return { type: SectionType.DEFAULT };
    }

    // Look for data-type attribute in the content
    const dataTypeMatch = sectionContent.match(/data-type="([^"]+)"/);

    if (dataTypeMatch) {
      const dataType = dataTypeMatch[1];

      // Check if it's a codeblock type
      if (dataType === 'codeblock') {
        return {
          type: SectionType.CODEBLOCK,
          dataType,
        };
      }
    }

    // Default section type
    return { type: SectionType.DEFAULT };
  } catch (error) {
    return { type: SectionType.DEFAULT };
  }
};

/**
 * Check if a section is a codeblock type
 *
 * @param sectionContent - HTML content of the section
 * @returns boolean - True if section is a codeblock
 */
export const isCodeblockSection = (sectionContent: string): boolean => {
  const result = detectSectionType(sectionContent);
  return result.type === SectionType.CODEBLOCK;
};

/**
 * Extract data attributes from section content
 *
 * @param sectionContent - HTML content of the section
 * @returns ElementAttributes - Parsed attributes object
 */
export const extractSectionAttributes = (
  sectionContent: string,
): ElementAttributes => {
  const attributes: ElementAttributes = {};

  try {
    // Extract data-id
    const idMatch = sectionContent.match(/data-id="([^"]+)"/);
    if (idMatch) {
      attributes.id = idMatch[1];
    }

    // Extract data-type
    const typeMatch = sectionContent.match(/data-type="([^"]+)"/);
    if (typeMatch) {
      attributes.dataType = typeMatch[1];
    }

    // Extract styling attributes
    const bgColorMatch = sectionContent.match(/data-bg-color="([^"]+)"/);
    if (bgColorMatch) {
      attributes.dataBgColor = bgColorMatch[1];
    }

    // Extract padding attributes
    const paddingTopMatch = sectionContent.match(/data-padding-top="([^"]+)"/);
    if (paddingTopMatch) {
      attributes.dataPaddingTop = paddingTopMatch[1];
    }

    const paddingRightMatch = sectionContent.match(
      /data-padding-right="([^"]+)"/,
    );
    if (paddingRightMatch) {
      attributes.dataPaddingRight = paddingRightMatch[1];
    }

    const paddingBottomMatch = sectionContent.match(
      /data-padding-bottom="([^"]+)"/,
    );
    if (paddingBottomMatch) {
      attributes.dataPaddingBottom = paddingBottomMatch[1];
    }

    const paddingLeftMatch = sectionContent.match(
      /data-padding-left="([^"]+)"/,
    );
    if (paddingLeftMatch) {
      attributes.dataPaddingLeft = paddingLeftMatch[1];
    }

    // Extract margin attributes
    const marginTopMatch = sectionContent.match(/data-margin-top="([^"]+)"/);
    if (marginTopMatch) {
      attributes.dataMarginTop = marginTopMatch[1];
    }

    const marginRightMatch = sectionContent.match(
      /data-margin-right="([^"]+)"/,
    );
    if (marginRightMatch) {
      attributes.dataMarginRight = marginRightMatch[1];
    }

    const marginBottomMatch = sectionContent.match(
      /data-margin-bottom="([^"]+)"/,
    );
    if (marginBottomMatch) {
      attributes.dataMarginBottom = marginBottomMatch[1];
    }

    const marginLeftMatch = sectionContent.match(/data-margin-left="([^"]+)"/);
    if (marginLeftMatch) {
      attributes.dataMarginLeft = marginLeftMatch[1];
    }

    return attributes;
  } catch (error) {
    return attributes;
  }
};

/**
 * Check if a section has custom CSS or JS (EJS) content
 *
 * @param section - SiteSection object with potential css and js fields
 * @returns boolean - True if section has custom content to process
 */
export const hasCustomContent = (section: {
  css?: string;
  ejs?: string;
}): boolean => {
  return Boolean(section.css?.trim()) || Boolean(section.ejs?.trim());
};

/**
 * Determine if a section needs special processing
 * Combines type detection and custom content checking
 *
 * @param section - SiteSection object
 * @returns boolean - True if section needs special codeblock processing
 */
export const needsCodeblockProcessing = (section: {
  content: string;
  css?: string;
  ejs?: string;
}): boolean => {
  return isCodeblockSection(section.content) && hasCustomContent(section);
};

/**
 * Extract the inner element from section content
 * Specifically looks for the div with data-type="codeblock"
 *
 * @param sectionContent - HTML content of the section
 * @returns string | null - Inner element HTML or null if not found
 */
export const extractCodeblockElement = (
  sectionContent: string,
): string | null => {
  try {
    // Look for the div with data-type="codeblock"
    const codeblockMatch = sectionContent.match(
      /<div[^>]*data-type="codeblock"[^>]*>([\s\S]*?)<\/div>/,
    );

    if (codeblockMatch) {
      return codeblockMatch[0]; // Return the entire div element
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Build inline styles from data attributes
 *
 * @param attributes - Element attributes object
 * @returns string - CSS style string
 */
export const buildInlineStyles = (attributes: ElementAttributes): string => {
  const styles: string[] = [];

  if (attributes.dataBgColor) {
    styles.push(`background-color: ${attributes.dataBgColor}`);
  }

  // Note: Do not apply padding from data attributes. HTML controls all spacing.

  // Build margin
  // const marginValues = [
  //   attributes.dataMarginTop || '0px',
  //   attributes.dataMarginRight || '0px',
  //   attributes.dataMarginBottom || '0px',
  //   attributes.dataMarginLeft || '0px',
  // ];
  // if (marginValues.some(val => val !== '0px')) {
  //   styles.push(`margin: ${marginValues.join(' ')}`);
  // }

  return styles.join('; ');
};

// Export types for use in other modules
export type { SectionTypeResult, ElementAttributes };
